import { useEffect, useState } from "react";
import { Avatar } from "primereact/avatar";

/**
 * GTK-748 — celda de persona/host de la pantalla de Asistencias.
 *
 * El diseño muestra un círculo de color con DOS iniciales, no la inicial sola
 * sobre el gris por defecto de PrimeReact. El color sale del nombre, así que la
 * misma persona se ve siempre igual en la tabla, en el modal de historial y en
 * el de edición, sin pedirle nada al backend.
 *
 * Cuando el backend manda foto (`avatar_url`), la foto gana y las iniciales
 * quedan de fallback.
 */

/** Tonos con contraste suficiente para texto blanco (WCAG AA en 13px bold). */
const AVATAR_COLORS = [
  "#B45309",
  "#2F80ED",
  "#5B5BD6",
  "#0F9D8C",
  "#C2410C",
  "#7C3AED",
  "#0369A1",
  "#9D174D",
];

export const personInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

const colorForName = (name: string): string => {
  const sum = Array.from(name).reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
};

interface UserAvatarProps {
  /** Nombre del que salen las iniciales y el color de fondo. */
  name: string;
  avatarUrl?: string | null;
  /** Lado del avatar en px. 32 en tablas, 44 en las cabeceras de modal. */
  size?: number;
}

/**
 * Círculo de avatar: la foto si hay y carga; si no hay, o la URL falla (objeto
 * borrado en S3, foto vieja de un dominio externo que ya no responde), las
 * iniciales. Nunca queda un ícono de imagen rota (GTK-804).
 */
export const UserAvatar = ({ name, avatarUrl, size = 32 }: UserAvatarProps) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [avatarUrl]);

  const showImage = !!avatarUrl && !failed;
  return (
    <Avatar
      image={showImage ? avatarUrl : undefined}
      label={showImage ? undefined : personInitials(name)}
      onImageError={() => setFailed(true)}
      imageAlt={name}
      shape="circle"
      style={{
        width: size,
        height: size,
        flex: "0 0 auto",
        backgroundColor: showImage ? undefined : colorForName(name),
        color: "#fff",
        fontSize: size >= 44 ? "var(--font-body-size)" : "var(--font-small-size)",
        fontWeight: 600,
      }}
    />
  );
};

interface PersonCellProps {
  /** Null o vacío renderiza el guión de "sin dato", como el resto de la tabla. */
  name?: string | null;
  avatarUrl?: string | null;
  /** Segunda línea opcional: email, subtipo del evento, etc. */
  secondaryLine?: string | null;
  /** Lado del avatar en px. 32 en tablas, 44 en las cabeceras de modal. */
  size?: number;
}

export const PersonCell = ({ name, avatarUrl, secondaryLine, size = 32 }: PersonCellProps) => {
  if (!name) return <span className="text-500">—</span>;

  return (
    <div className="flex align-items-center gap-2">
      <UserAvatar name={name} avatarUrl={avatarUrl} size={size} />
      <div className="flex flex-column">
        <span className="font-medium">{name}</span>
        {secondaryLine && <span className="attendances-cell-sub text-600">{secondaryLine}</span>}
      </div>
    </div>
  );
};
